import { useState } from "react";
import { Search, XCircle, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { BigButton, inputCls } from "@/components/ui-kit";

const detail = (e) => e?.response?.data?.detail;

export function ManageBooking({ onReschedule }) {
  const { t } = useApp();
  const [code, setCode] = useState("");
  const [bk, setBk] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { const d = detail(e); toast.error(["booking_not_found", "booking_not_editable", "slot_taken"].includes(d) ? t(d) : errMsg(e)); } finally { setBusy(false); } };
  const find = () => code.trim() && run(async () => setBk((await api.get(`/public/bookings/${code.trim()}`)).data));
  const cancel = () => window.confirm(t("booking_cancel_confirm")) && run(async () => { setBk((await api.post(`/public/bookings/${bk.code}/cancel`)).data); toast.success(t("booking_cancelled")); });
  return (
    <div className="rounded-2xl border border-white/15 bg-white/5 backdrop-blur-md p-4 space-y-3" data-testid="manage-booking">
      <div className="font-heading text-xl">{t("manage_booking")}</div>
      <div className="flex gap-2">
        <input dir="ltr" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && find()} placeholder={t("booking_code_label")} maxLength={6} className={`${inputCls} text-foreground tracking-widest font-bold`} data-testid="manage-code-input" />
        <BigButton variant="gold" disabled={busy || code.trim().length < 4} onClick={find} className="whitespace-nowrap shrink-0" data-testid="manage-find-btn"><Search className="w-5 h-5" />{t("find_booking")}</BigButton>
      </div>
      {bk && (
        <div className="rounded-xl bg-white text-foreground p-4 space-y-2" data-testid="manage-booking-card">
          <div className="flex items-center justify-between gap-2"><span className="text-2xl font-black tracking-widest" dir="ltr">{bk.code}</span><span className={`px-2 py-0.5 rounded text-white text-xs font-bold ${bk.status === "cancelled" ? "bg-[#C7353B]" : "bg-[#1F9D55]"}`} data-testid="manage-booking-status">{t(`appt_${bk.status}`)}</span></div>
          <div className="font-bold">{bk.clinic.name} · {bk.doctor}</div>
          <div dir="ltr" className="font-bold" data-testid="manage-booking-when">{bk.date} {bk.time}</div>
          <div className="text-sm text-[#555]">{t("patient")}: {bk.patient}{bk.reason && ` · ${bk.reason}`}</div>
          {bk.editable ? (
            <div className="grid sm:grid-cols-2 gap-2 pt-1">
              <BigButton variant="gold" disabled={busy} onClick={() => onReschedule(bk)} data-testid="manage-reschedule-btn"><CalendarClock className="w-5 h-5" />{t("reschedule_booking")}</BigButton>
              <BigButton variant="danger" disabled={busy} onClick={cancel} data-testid="manage-cancel-btn"><XCircle className="w-5 h-5" />{t("cancel_booking")}</BigButton>
            </div>
          ) : <p className="text-xs text-[#555]" data-testid="manage-not-editable">{t("booking_not_editable")}</p>}
        </div>
      )}
    </div>
  );
}
